"use client";
import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";

import { useQuery, useQueryClient, useMutation } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import { getPersonalContacts, deletePersonalContact, getPersonalContactById } from "../../../api/services/Client";

import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import ContactModal from "./ContactModal";
import DataTable from "../../Table/DataTable";

const ContactTable = () => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [modalData, setModalData] = useState({});
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError, error } = useQuery([QK.personalContacts], getPersonalContacts);

  const deleteMutation = useMutation(deletePersonalContact, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.personalContacts]);
      enqueueSnackbar('Contact deleted successfully', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete contact', { variant: 'error' });
    },
  });

  const fetchContactById = async (contactId) => {
    try {
      const response = await getPersonalContactById(contactId);
      setModalData(response);
      setShowModal(true);
    } catch (error) {
      enqueueSnackbar('Failed to fetch contact details', { variant: 'error' });
    }
  };

  const handleEdit = async (contact) => {
    await fetchContactById(contact.id);
  };

  const handleDelete = (contactId) => {
    setSelectedContact(contactId);
    setShowConfirmModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setShowConfirmModal(false);
    setSelectedContact(null);
    setModalData({});
  };

  const handleConfirm = () => {
    if (selectedContact) {
      deleteMutation.mutate(selectedContact);
    }
    handleClose();
  };

  const headers = [
    { key: "name", label: "Name" },
    { key: "relationship", label: "Relationship / Role" },
    { key: "contact", label: "Contact Info" },
    { key: "portalAccess", label: "Portal Access" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "name") {
      return (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: "primary.light", color: "primary.main", width: 32, height: 32 }}>
            {item.first_name?.[0]}{item.last_name?.[0]}
          </Avatar>
          <Typography variant="body2" fontWeight={600} color="#0F172A">
            {item.first_name} {item.last_name}
          </Typography>
        </Stack>
      );
    }
    if (header.key === "relationship") {
      return (
        <Stack spacing={0.5}>
          <Chip label={item.relationship} size="small" sx={{ fontWeight: 600, width: "fit-content", bgcolor: "#EFF6FF", color: "#1D4ED8" }} />
          <Typography variant="caption" color="text.secondary">{item.role}</Typography>
        </Stack>
      );
    }
    if (header.key === "contact") {
      return (
        <Box>
          <Typography variant="body2" color="#334155">{item.mobile || "N/A"}</Typography>
          <Typography variant="caption" color="#94A3B8">{item.otherPhone || "No other phone"}</Typography>
        </Box>
      );
    }
    if (header.key === "portalAccess") {
      return item.portalAccess ? (
        <Chip icon={<CheckCircleIcon style={{ color: '#10b981' }} />} label="Active" variant="outlined" color="success" size="small" sx={{ borderRadius: "6px" }} />
      ) : (
        <Chip icon={<CancelIcon style={{ color: '#94a3b8' }} />} label="Disabled" variant="outlined" size="small" sx={{ borderRadius: "6px", color: '#94a3b8' }} />
      );
    }
    return item[header.key] || "-";
  };

  const customActions = (item) => (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      <Tooltip title="Edit Contact" arrow>
        <IconButton
          size="small"
          onClick={() => handleEdit(item)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#F59E0B",
              bgcolor: alpha("#F59E0B", 0.06),
            },
          }}
        >
          <EditTwoToneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => handleDelete(item.id)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#EF4444",
              bgcolor: alpha("#EF4444", 0.06),
            },
          }}
        >
          <DeleteTwoToneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error" sx={{ my: 2 }}>Error: {error.message}</Alert>;
  }

  const contacts = data?.results || [];

  return (
    <>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "#FAFBFC",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} color="#0F172A">
          Personal Contacts
        </Typography>
      </Box>

      <DataTable
        data={contacts}
        headers={headers}
        renderCell={renderCell}
        customActions={customActions}
        loading={isLoading}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        title="Confirm Delete"
        body="Are you sure you want to delete this contact? This action cannot be undone."
      />
      
      <ContactModal
        show={showModal}
        onHide={handleClose}
        data={modalData}
      />
    </>
  );
};

export default ContactTable;